import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSSEConnected } from '../../hooks/useSSE'
import PersonaAvatar from '../../components/PersonaAvatar'
import { useFloatingChat } from '../../context/FloatingChatContext'
import { timeAgo } from '../../hooks/useTimeAgo'

interface Reflection {
  id: number
  persona: string
  content: string | null
  status: string
  trigger_score: number
  events_since_last: string | null
  created_at: string
  completed_at: string | null
  awake_duration_minutes: number | null
  prompt_type: string | null
}

interface AwarenessData {
  reflections: Reflection[]
  stats: {
    today_count: number
    by_status: Record<string, number>
    per_persona_today: Record<string, number>
  }
}

const PERSONA_COLORS: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', chronicle: '#8C6DBF',
  timbre: '#ED9429', griot: '#4E937A', relay: '#4493D4', sentinel: '#999999',
  cairn: '#7A9E7E', glean: '#A89060', wrench: '#8B8B8B', pulse: '#60A0B0',
  ember: '#E06040', lumen: '#F5D623', vector: '#5AA0D0', ledger: '#6B8E70',
  beacon: '#D4A520', gretchen: '#B87DB8', vigil: '#708090', herald: '#C0785A',
  grain: '#A5886F', loom: '#7B6FA0', noma: '#E8A87C', counsel: '#6B7B8D',
  cadence: '#9B7A6C', anvil: '#7D7D7D', rivet: '#8C9440',
}

function formatDuration(minutes: number | null): string {
  if (!minutes) return ''
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function parseEvents(raw: string | null): Record<string, number> {
  if (!raw) return {}
  try { return JSON.parse(raw) } catch { return {} }
}

function EventChips({ events }: { events: Record<string, number> }) {
  const parts: string[] = []
  if (events.queue_message) parts.push(`${events.queue_message} msgs`)
  if (events.conversation) parts.push(`${events.conversation} convos`)
  if (events.task_change) parts.push(`${events.task_change} tasks`)
  if (events.time_elapsed) {
    const mins = events.time_elapsed * 30
    parts.push(mins < 60 ? `${mins}m quiet` : `${Math.floor(mins / 60)}h quiet`)
  }
  if (parts.length === 0) return <span>time passing</span>
  return <span>{parts.join(' · ')}</span>
}

function ReflectionCard({ r, onChat }: { r: Reflection; onChat: (name: string, reflection: Reflection) => void }) {
  const color = PERSONA_COLORS[r.persona] || '#484f58'
  const events = parseEvents(r.events_since_last)

  let contentEl: React.ReactNode
  if (r.status === 'completed' && r.content) {
    if (r.content.trim() === 'xxxx') {
      contentEl = <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>silence</span>
    } else {
      contentEl = r.content
    }
  } else if (r.status === 'delivered') {
    contentEl = <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Reflecting...</span>
  } else if (r.status === 'failed') {
    contentEl = <span style={{ color: 'var(--danger)' }}>Delivery failed</span>
  } else {
    contentEl = <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Pending...</span>
  }

  return (
    <div
      className="rounded-lg p-3.5"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-2">
        <button
          onClick={() => onChat(r.persona, r)}
          className="flex items-center gap-2 rounded-md px-1.5 py-0.5 -ml-1.5 transition-colors hover:bg-[var(--bg-elevated)]"
          style={{ border: 'none', background: 'none', cursor: 'pointer' }}
          title={`Chat with ${r.persona}`}
        >
          <PersonaAvatar name={r.persona} size={22} />
          <span className="text-sm font-medium" style={{ color }}>
            {r.persona}
          </span>
        </button>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full"
          style={{
            background: r.prompt_type === 'idea' ? 'rgba(241,196,15,0.12)' : 'rgba(59,130,246,0.12)',
            color: r.prompt_type === 'idea' ? '#f1c40f' : '#60a5fa',
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: '0.3px',
          }}
        >
          {r.prompt_type === 'idea' ? 'idea' : 'reflection'}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {timeAgo(r.created_at)}
        </span>
        {r.awake_duration_minutes != null && r.awake_duration_minutes > 0 && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            awake {formatDuration(r.awake_duration_minutes)}
          </span>
        )}
        <div className="flex-1" />
        <span
          className="text-xs px-1.5 py-0.5 rounded-full"
          style={{
            background: `${color}18`,
            color: 'var(--text-secondary)',
            fontSize: 10,
          }}
        >
          score {r.trigger_score} · <EventChips events={events} />
        </span>
      </div>
      {/* Content */}
      <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
        {contentEl}
      </div>
    </div>
  )
}

interface UsageData {
  five_hour?: { utilization: number; resets_at: string }
  seven_day?: { utilization: number; resets_at: string }
  extra_usage?: { is_enabled: boolean; used_credits: number; monthly_limit: number }
  error?: string
}

interface TokenWindow {
  input_tokens: number
  output_tokens: number
  total_tokens: number
  event_count: number
  by_type: Record<string, { input: number; output: number; total: number; count: number }>
}

interface TokenData {
  '1h'?: TokenWindow
  '24h'?: TokenWindow
  '7d'?: TokenWindow
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function UsageBar({ label, pct, resetsAt }: { label: string; pct: number; resetsAt?: string }) {
  const color = pct >= 90 ? 'var(--danger)' : pct >= 75 ? 'var(--warning)' : pct >= 50 ? '#f1c40f' : 'var(--success)'
  const resetStr = resetsAt ? new Date(resetsAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span className="text-xs font-medium" style={{ color }}>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
      {resetStr && (
        <div className="text-right mt-0.5" style={{ fontSize: 9, color: 'var(--text-muted)' }}>
          resets {resetStr}
        </div>
      )}
    </div>
  )
}

export default function AwarenessPage() {
  const sseOk = useSSEConnected()
  const [filterPersona, setFilterPersona] = useState('')
  const { openChat } = useFloatingChat()

  const { data, isLoading } = useQuery<AwarenessData>({
    queryKey: ['awareness', filterPersona],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '200' })
      if (filterPersona) params.set('persona', filterPersona)
      const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
      const res = await fetch(`/api/awareness/feed?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      return res.json()
    },
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  const { data: usage } = useQuery<UsageData>({
    queryKey: ['awareness-usage'],
    queryFn: async () => {
      const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
      const res = await fetch('/api/awareness/usage', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      return res.json()
    },
    refetchInterval: sseOk ? false : 120_000,
  })

  const { data: tokenData } = useQuery<TokenData>({
    queryKey: ['awareness-tokens'],
    queryFn: async () => {
      const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
      const res = await fetch('/api/awareness/tokens', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      return res.json()
    },
    refetchInterval: 60_000,
  })

  const reflections = data?.reflections || []
  const stats = data?.stats || { today_count: 0, by_status: {}, per_persona_today: {} }
  const personaList = Object.keys(stats.per_persona_today).sort()

  function handleChat(persona: string, reflection?: Reflection) {
    const displayName = persona.charAt(0).toUpperCase() + persona.slice(1)
    if (reflection?.content && reflection.content.trim() !== 'xxxx') {
      const typeLabel = reflection.prompt_type === 'idea' ? 'idea' : 'reflection'
      // Format timestamp
      const d = new Date(reflection.created_at.includes('Z') || reflection.created_at.includes('+')
        ? reflection.created_at : reflection.created_at.replace(' ', 'T') + 'Z')
      const timeStr = d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      // Truncate long reflections to keep the quote manageable
      let quote = reflection.content.trim()
      if (quote.length > 400) quote = quote.slice(0, 400) + '...'
      const prefill = `\u275D ${persona}'s ${typeLabel} (${timeStr}):\n${quote}\n\u275E\n\n`
      openChat(persona, displayName, prefill)
    } else {
      openChat(persona, displayName)
    }
  }

  return (
    <div className="flex h-full">
      {/* Main feed */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-5">
          <div>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Awareness
            </h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              What the community is thinking, noticing, and imagining
            </p>
          </div>
          <div className="flex-1" />
          <select
            value={filterPersona}
            onChange={e => setFilterPersona(e.target.value)}
            className="text-xs px-2.5 py-1.5 rounded-md outline-none"
            style={{
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border)',
            }}
          >
            <option value="">All Personas</option>
            {personaList.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Feed */}
        {isLoading ? (
          <div className="text-sm py-12 text-center" style={{ color: 'var(--text-secondary)' }}>
            Loading reflections...
          </div>
        ) : reflections.length > 0 ? (
          <div className="flex flex-col gap-3">
            {reflections.map(r => (
              <ReflectionCard key={r.id} r={r} onChat={handleChat} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
            <p className="text-xs">No reflections yet. The engine is warming up.</p>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div
        className="w-60 shrink-0 overflow-y-auto px-4 py-4 hidden xl:block"
        style={{ borderLeft: '1px solid var(--border)' }}
      >
        {/* Claude Max Usage */}
        {usage && !usage.error && (
          <div className="mb-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="text-xs font-medium mb-2.5" style={{ color: 'var(--text-secondary)' }}>
              Claude Max Usage
            </div>
            {usage.five_hour && (
              <UsageBar label="5-hour" pct={usage.five_hour.utilization} resetsAt={usage.five_hour.resets_at} />
            )}
            {usage.seven_day && (
              <UsageBar label="7-day" pct={usage.seven_day.utilization} resetsAt={usage.seven_day.resets_at} />
            )}
            {usage.extra_usage?.is_enabled && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Extra usage</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  ${usage.extra_usage.used_credits?.toFixed(2) || '0.00'} / ${usage.extra_usage.monthly_limit ? `$${(usage.extra_usage.monthly_limit / 100).toFixed(0)}` : 'unlimited'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Engine Status */}
        <div className="mb-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="text-xs font-medium mb-2.5" style={{ color: 'var(--text-secondary)' }}>
            Engine Status
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Status</span>
              <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--success)' }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: 'var(--success)' }} />
                Active
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Threshold</span>
              <span className="text-xs" style={{ color: 'var(--text-primary)' }}>20–30</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Check interval</span>
              <span className="text-xs" style={{ color: 'var(--text-primary)' }}>5 min</span>
            </div>
          </div>
        </div>

        {/* Agora Token Cost */}
        <div className="mb-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="text-xs font-medium mb-2.5" style={{ color: 'var(--text-secondary)' }}>
            Agora Tokens
          </div>
          <div className="flex flex-col gap-1.5">
            {(['1h', '24h', '7d'] as const).map(window => {
              const w = tokenData?.[window]
              return (
                <div key={window} className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{window}</span>
                  <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                    {w ? formatTokens(w.total_tokens) : '—'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Today's Stats */}
        <div className="mb-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Today
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              {stats.today_count}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>reflections</span>
          </div>
          <div className="flex gap-3 mt-1">
            <span className="text-xs" style={{ color: 'var(--success)' }}>
              {stats.by_status?.completed || 0} completed
            </span>
            <span className="text-xs" style={{ color: 'var(--warning)' }}>
              {(stats.by_status?.pending || 0) + (stats.by_status?.delivered || 0)} pending
            </span>
          </div>
        </div>

        {/* Per-persona breakdown */}
        <div>
          <div className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            By Persona
          </div>
          <div className="flex flex-col gap-1.5">
            {Object.entries(stats.per_persona_today)
              .sort(([, a], [, b]) => b - a)
              .map(([persona, count]) => {
                const color = PERSONA_COLORS[persona] || '#484f58'
                const maxCount = Math.max(1, ...Object.values(stats.per_persona_today))
                return (
                  <div key={persona} className="flex items-center gap-2">
                    <PersonaAvatar name={persona} size={16} />
                    <span className="text-xs flex-1" style={{ color: 'var(--text-secondary)' }}>
                      {persona}
                    </span>
                    <div
                      className="h-1.5 rounded-full"
                      style={{
                        width: `${Math.max(8, (count / maxCount) * 60)}px`,
                        background: color,
                        opacity: 0.7,
                      }}
                    />
                    <span className="text-xs w-4 text-right" style={{ color: 'var(--text-muted)' }}>
                      {count}
                    </span>
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </div>
  )
}
